const _ = require("lodash");
const Helpers = require("../canvasmath/util/ParserHelpers");
const Numbers = require("../../../shared/Numbers");

// Takes a string and turns it into and object with whole, numerator,
// denominator
const parseNumber = function(numString) {
	if (_.isObject(numString)) {
		return numString;
	}
	numString = numString.trim();
	numString = numString.replace(/,/g, "");
	let whole = null;
	let numerator = null;
	let denominator = null;
	if (numString.indexOf(" ") > -1) {
		whole = parseInt(numString.split(" ")[0]);
		numString = numString.split(" ")[1];
	}
	if (numString.indexOf("/") > -1) {
		numerator = parseInt(numString.split("/")[0]);
		denominator = parseInt(numString.split("/")[1]);
	}
	if (!whole && !numerator) {
		whole = parseInt(numString);
	}
	return {
		whole: _.isNaN(whole) ? null : whole,
		numerator: _.isNaN(numerator) ? null : numerator,
		denominator: _.isNaN(denominator) ? null : denominator,
	};
};

// Takes in object with props whole, numerator, denominator
// Converts to string
const parseString = function(obj) {
	if (!_.isObject(obj)) {
		return obj;
	}
	let numString = "";
	numString += _.isNumber(obj.whole) ? obj.whole + " " : "";
	numString += _.isNumber(obj.numerator) ? obj.numerator : "";
	numString +=
		_.isNumber(obj.numerator) || _.isNumber(obj.denominator) ? "/" : "";
	numString += _.isNumber(obj.denominator) ? obj.denominator : "";
	return numString.trim();
};

// Takes in object with props whole, numerator, denominator. If a string, turns
// string into object first, then calculates. calculates the numeric value of
// it.
const calcNumber = function(objOrString) {
	const obj = parseNumber(objOrString);

	const whole = obj.whole || 0;
	const numerator = obj.numerator || 0;
	const denominator = obj.denominator || 1;

	return whole + numerator / denominator;
};

//Takes in object with props whole, numerator, denominator and makes it into
//batex fraction form.
const makeBatex = function(obj, settings = {}) {
	const {noDollars = false} = settings;
	let batex = "";
	if (_.isInteger(obj.whole)) {
		batex += Helpers.withCommas(obj.whole) + " ";
	}
	if (
		_.isInteger(obj.numerator) &&
		_.isInteger(obj.denominator) &&
		obj.numerator
	) {
		batex +=
			"\\frac{" +
			Helpers.withCommas(obj.numerator) +
			"}{" +
			Helpers.withCommas(obj.denominator) +
			"}";
	} else if (obj.numerator === 0 && !batex) {
		batex += "0";
	}
	if (noDollars) {
		return batex.trim();
	}
	return "$" + batex.trim() + "$";
};

//Takes in string and makes it into batex fraction form.
const makeBatexFromString = function(str, settings) {
	return makeBatex(parseNumber(str));
};

const makeFractionFromPercent = function(perc) {
	if (!perc && perc !== 0) return null;
	const str = perc.toString();
	let frac;
	if (_.includes(str, ".")) {
		const power = str.length - 1 - str.indexOf(".");
		frac = {
			numerator: parseInt(str.split(".").join("")),
			denominator: 100 * Math.pow(10, power),
		};
	} else {
		frac = parseNumber(str);
		if (frac.whole) {
			frac.numerator = frac.numerator || 0;
			frac.denominator = frac.denominator || 1;
			frac.numerator += frac.denominator * frac.whole;
			frac.denominator *= 100;
		}
	}
	frac = simplifyFraction(frac);
	return frac;
};

// convert mixed number to improper fraction
const makeImproper = function(obj) {
	if (_.isNull(obj.whole)) {
		return obj;
	}
	const numerator = obj.numerator || 0;
	const denominator = obj.denominator || 1;
	const whole = obj.whole || 0;

	return {
		whole: null,
		numerator: denominator * whole + numerator,
		denominator: denominator,
	};
};

// convert improper fraction to mixed number
const makeMixed = function(obj) {
	obj = _.extend({whole: null}, obj);
	const numerator = Math.abs(obj.numerator);
	const denominator = Math.abs(obj.denominator);
	const whole = obj.whole;
	if (numerator < denominator || whole !== null) {
		return obj;
	}
	const sign = obj.numerator / obj.denominator >= 0 ? 1 : -1;
	return {
		whole: Math.floor(numerator / denominator) * sign,
		numerator: numerator % denominator,
		denominator: denominator,
	};
};

// takes fraction object with numerator and denominator keys and returns a
// fraction object with simplified numerator and denominator
const simplifyFraction = function(frac) {
	const g = gcd(frac.numerator, frac.denominator);
	return {numerator: frac.numerator / g, denominator: frac.denominator / g};
};

// Takes a numeric value and turns it into string fraction format.
// 1.5 -> "3/2"
const makeFractionFromNumber = function(number, denominator) {
	return Math.round(number * denominator) + "/" + denominator;
};

//Type is either "fraction" or "mixed"
const makeObjFromNumber = function(number, denominator, type) {
	const fracObj = parseNumber(makeFractionFromNumber(number, denominator));
	if (type === "fraction") {
		return fracObj;
	} else if (type === "mixed") {
		return makeMixed(fracObj);
	}
};

// Checks whether two fractions are numerically equivalent by putting them in
// simplest form and comparing numerator and denominator.
const areEquivalent = function(f1, f2) {
	const improperF1 = makeImproper(f1);
	const improperF2 = makeImproper(f2);
	const gcdF1 = gcd(improperF1.numerator, improperF1.denominator);
	const gcdF2 = gcd(improperF2.numerator, improperF2.denominator);

	// Checks whether numerators are oppositely signed (e.g. f1 is < 0, f2 >= 0)
	// and swaps f1 signs if true
	if (
		(improperF1.numerator < 0 || improperF2.numerator < 0) &&
		!(improperF1.numerator < 0 && improperF2.numerator < 0)
	) {
		improperF1.numerator = -improperF1.numerator;
		improperF1.denominator = -improperF1.denominator;
	}

	return (
		improperF1.numerator / gcdF1 === improperF2.numerator / gcdF2 &&
		improperF1.denominator / gcdF1 === improperF2.denominator / gcdF2
	);
};

//Adjusted from http://www.w3resource.com/javascript-exercises/javascript-math-exercise-10.php
const lcm = function(x, y) {
	return !x || !y ? 0 : Math.abs((x * y) / gcd(x, y));
};

//Adjusted from http://www.w3resource.com/javascript-exercises/javascript-math-exercise-10.php
const gcd = function(x, y) {
	x = Math.abs(x);
	y = Math.abs(y);
	while (y) {
		const t = y;
		y = x % y;
		x = t;
	}
	return x;
};

//Frac is fraction object
const getType = function(frac) {
	if (
		!frac.hasOwnProperty("whole") &&
		!frac.hasOwnProperty("numerator") &&
		!frac.hasOwnProperty("denominator")
	) {
		return "error";
	}

	if (
		!_.isNull(frac.whole) &&
		!_.isNull(frac.numerator) &&
		!_.isNull(frac.denominator)
	) {
		return "mixed";
	} else if (
		!_.isNull(frac.whole) &&
		_.isNull(frac.numerator) &&
		_.isNull(frac.denominator)
	) {
		return "whole";
	} else if (
		_.isNull(frac.whole) &&
		!_.isNull(frac.numerator) &&
		!_.isNull(frac.denominator)
	) {
		return "fraction";
	}
	return "error";
};

//Check that a fraction object (an object with numerator and denominator keys)
//is valid by seeing if both keys are numbers, not NaN, not infinity, and that
//the denominator isn't 0.
const isValidImproperFraction = function(frac) {
	if (
		!_.isFinite(frac.numerator) ||
		!_.isFinite(frac.denominator) ||
		frac.denominator === 0
	) {
		return false;
	}
	return true;
};

//Checks if the fractional part of either just a fraction or mixed number is unsimplified
const isFractionalPartUnsimplified = function(frac) {
	return gcd(frac.numerator, frac.denominator) > 1 || frac.denominator === 1;
};

const isFractionalPartImproper = function(frac) {
	return frac.numerator >= frac.denominator;
};

//Finds the lowest multiple of multiple that is greater than or equal to start
//start and multiple must both be in improper fraction form
const firstMultipleAbove = function(start, multiple) {
	const quotient = Math.ceil(
		(start.numerator * multiple.denominator) /
			(start.denominator * multiple.numerator)
	);
	return {
		whole: null,
		numerator: quotient * multiple.numerator,
		denominator: multiple.denominator,
	};
};

const isLessThanOrEqualTo = function(f1, f2) {
	const f1DenSign = Numbers.sign(f1.denominator);
	const f2DenSign = Numbers.sign(f2.denominator);
	return (
		f1.numerator * Math.abs(f2.denominator) * f1DenSign <=
		Math.abs(f1.denominator) * f2.numerator * f2DenSign
	);
};

// Doesn't support mixed numbers
const plus = function(f1, f2) {
	const denLCM = lcm(f1.denominator, f2.denominator);
	return {
		whole: null,
		numerator:
			f1.numerator * (denLCM / f1.denominator) +
			f2.numerator * (denLCM / f2.denominator),
		denominator: denLCM,
	};
};

const makeSortByFunc = function(frac) {
	return frac.numerator / frac.denominator;
};

const compare = function(f1, f2) {
	if (areEquivalent(f1, f2)) {
		return 0;
	} else if (isLessThanOrEqualTo(f1, f2)) {
		return -1;
	} else {
		return 1;
	}
};

const isWhole = function(f) {
	return !f.numerator;
};

const isNegative = function(f) {
	const imp = makeImproper(f);
	return imp.numerator < 0;
};

const makeFractionHaveDenominator = function(frac, newDenominator) {
	return {
		whole: frac.whole,
		numerator: frac.numerator * (newDenominator / frac.denominator),
		denominator: newDenominator,
	};
};

//Returns the type of number (whole, fraction, mixed), from a string value.
//e.g. "5 1/3" returns "mixed"
const getTypeFromString = function(str) {
	let type;
	if (_.includes(str, "/")) {
		type = _.includes(str, " ") ? "mixed" : "fraction";
	} else {
		type = "simple";
	}
	return type;
};

//Returns the sizes of the digits for the whole and fractional parts of fraction
//object
const getDigitSizes = function(obj) {
	const digitSizes = _.mapValues(obj, val =>
		_.isInteger(val) ? val.toString().length : 0
	);
	return {
		whole: digitSizes.whole,
		frac: Math.max(digitSizes.numerator, digitSizes.denominator),
	};
};

const isZeroDenominator = function(strOrObj) {
	let num = strOrObj;
	if (_.isString(strOrObj)) {
		num = parseNumber(strOrObj);
	}
	return num.denominator === 0;
};

const subtractImproperFractions = function(frac1, frac2) {
	return {
		numerator:
			frac1.numerator * frac2.denominator - frac2.numerator * frac1.denominator,
		denominator: frac2.denominator * frac1.denominator,
	};
};

// Multiply two fraction strings or objects and returns simplified fraction
// object
const multiplyFractions = function(frac1, frac2) {
	if (_.isString(frac1)) frac1 = parseNumber(frac1);
	if (_.isString(frac2)) frac2 = parseNumber(frac2);
	const prod = {
		whole: null,
		numerator: frac1.numerator * frac2.numerator,
		denominator: frac1.denominator * frac2.denominator,
	};
	return simplifyFraction(prod);
};

// Takes in a string and sees if the number has variables.
const hasVariables = function(numString) {
	return /[a-zA-Z]/.test(numString);
};

module.exports = {
	parseNumber,
	parseString,
	calcNumber,
	makeBatex,
	makeBatexFromString,
	makeImproper,
	makeMixed,
	simplifyFraction,
	makeFractionFromNumber,
	makeFractionFromPercent,
	makeObjFromNumber,
	lcm,
	gcd,
	getType,
	areEquivalent,
	isValidImproperFraction,
	firstMultipleAbove,
	isFractionalPartUnsimplified,
	isFractionalPartImproper,
	isLessThanOrEqualTo,
	plus,
	makeSortByFunc,
	compare,
	isWhole,
	isNegative,
	makeFractionHaveDenominator,
	getTypeFromString,
	getDigitSizes,
	isZeroDenominator,
	subtractImproperFractions,
	multiplyFractions,
	hasVariables,
};
